

import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line } from 'recharts';
import { useGetScoreSheetSummary } from 'service/comment.service';
import { isAtOrAbovePassMark } from 'utils/utils';
import useAuth from 'hooks/useAuth';
import { hasDpatFullScoreAccess, isDpatDistrictRestrictedViewer } from 'config/contextModules';
import { DPAT_BRAND, DPAT_CHART, tooltipStyle } from 'pages/dpat-dashboard/dpatChartTheme';
import {
    formatManualScoreDisplay,
    MANUAL_SCORE_FIELDS,
    readIndicatorManualScore,
    resolveRoleStandingCiStatuses,
    resolveRoleStandingNumericScores,
} from 'utils/dpatManualScoreHelpers';

const TOTAL_MAIN_CI_COUNT = 4;
const MANUAL_SCORE_COLOR = (value) => {
    const text = String(value ?? "").trim().toLowerCase();
    if (text === "fulfilled" || value === true) return "green";
    return "#cf1322";
};

function systemScoreCell(item) {
    if (item?.systemActualScore != null && item.systemActualScore !== "") {
        return item.systemActualScore;
    }
    if (item?.systemScore != null && item.systemScore !== "") {
        return item.systemScore;
    }
    return "—";
}

function ciOutcomeCell(fulfilled) {
    return fulfilled ? (
        <span style={{ color: 'green', fontWeight: 'bold' }}>✓ Fulfilled</span>
    ) : (
        <span style={{ color: 'red', fontWeight: 'bold' }}>✗ Not Fulfilled</span>
    );
}

function formatCiStandingLabel(fulfilled) {
    return fulfilled ? "Fulfilled" : "Not Fulfilled";
}

function roleManualScoreCell(item, field, kind = "numeric") {
    const isSubtotal = String(item?.no || "").toLowerCase().includes("sub-total");
    if (isSubtotal) {
        if (kind === "CI") return "—";
        const standingValue =
            field === MANUAL_SCORE_FIELDS.TECHNICAL_TEAM
                ? item.technicalTeamStandingScore ?? item.technicalTeamScore
                : field === MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE
                    ? item.qualityAssuranceStandingScore ?? item.qualityAssuranceScore
                    : item.petitionCommitteeStandingScore ?? item.petitionCommitteeScore;
        return <strong>{standingValue ?? 0}</strong>;
    }

    if (item?.childScoreAggregate) {
        const hasOverride =
            field === MANUAL_SCORE_FIELDS.TECHNICAL_TEAM
                ? item.technicalTeamHasOverride
                : field === MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE
                    ? item.qualityAssuranceHasOverride
                    : item.petitionCommitteeHasOverride;
        const standingValue =
            kind === "CI"
                ? field === MANUAL_SCORE_FIELDS.TECHNICAL_TEAM
                    ? item.technicalTeamStandingFulfilled
                    : field === MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE
                        ? item.qualityAssuranceStandingFulfilled
                        : item.petitionCommitteeStandingFulfilled
                : field === MANUAL_SCORE_FIELDS.TECHNICAL_TEAM
                    ? item.technicalTeamStandingScore
                    : field === MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE
                        ? item.qualityAssuranceStandingScore
                        : item.petitionCommitteeStandingScore;
        const displayValue =
            kind === "CI" ? formatCiStandingLabel(standingValue) : standingValue;
        if (kind === "CI") {
            return (
                <span style={{ color: MANUAL_SCORE_COLOR(standingValue), fontWeight: 'bold' }}>
                    {displayValue}
                </span>
            );
        }
        return hasOverride ? (
            <span style={{ color: MANUAL_SCORE_COLOR(displayValue), fontWeight: 'bold' }}>
                {displayValue}
            </span>
        ) : displayValue;
    }

    const entered = readIndicatorManualScore(item, field);

    if (kind === "CI") {
        const standing = resolveRoleStandingCiStatuses(item);
        const standingValue =
            field === MANUAL_SCORE_FIELDS.TECHNICAL_TEAM
                ? standing.technicalStanding
                : field === MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE
                    ? standing.qualityAssuranceStanding
                    : standing.petitionStanding;

        if (entered) {
            return (
                <span style={{ color: MANUAL_SCORE_COLOR(entered), fontWeight: 'bold' }}>
                    {formatManualScoreDisplay(entered)}
                </span>
            );
        }

        return (
            <span style={{ color: MANUAL_SCORE_COLOR(standingValue), fontWeight: 'bold' }}>
                {formatCiStandingLabel(standingValue)}
            </span>
        );
    }

    const standing = resolveRoleStandingNumericScores(item);
    const standingValue =
        field === MANUAL_SCORE_FIELDS.TECHNICAL_TEAM
            ? standing.technicalStanding
            : field === MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE
                ? standing.qualityAssuranceStanding
                : standing.petitionStanding;

    if (entered) {
        return (
            <span style={{ color: MANUAL_SCORE_COLOR(entered), fontWeight: 'bold' }}>
                {formatManualScoreDisplay(entered)}
            </span>
        );
    }

    return standingValue;
}


function countFulfilled(items) {
    return (items || []).filter((item) => item.fulfilled).length;
}

function buildComplianceChartData(fulfilledCount, totalCount) {
    const notFulfilledCount = Math.max(0, totalCount - fulfilledCount);
    return [
        { name: "Fulfilled", value: fulfilledCount, color: DPAT_CHART.emerald },
        { name: "Not Fulfilled", value: notFulfilledCount, color: DPAT_CHART.coral },
    ];
}

function ChartBox({ height = 260, children }) {
    return (
        <div className="dpat-summary-chart-box" style={{ width: "100%", height, overflow: "hidden" }}>
            <ResponsiveContainer width="100%" height="100%">
                {children}
            </ResponsiveContainer>
        </div>
    );
}

function ChartPanel({ title, children, wide = false }) {
    return (
        <div className={`dpat-summary-panel${wide ? " dpat-summary-chart-span" : ""}`}>
            <h4 className="dpat-summary-panel__title">{title}</h4>
            {children}
        </div>
    );
}

function KpiTile({ label, value, tone = "navy" }) {
    return (
        <div className={`dpat-summary-kpi dpat-summary-kpi--${tone}`}>
            <span className="dpat-summary-kpi__label">{label}</span>
            <strong className="dpat-summary-kpi__value">{value}</strong>
        </div>
    );
}

const axisTick = { fontSize: 11, fill: DPAT_BRAND };
const chartGrid = { stroke: "#e2e8f0", strokeDasharray: "3 3", vertical: false };
const barMargin = { top: 28, right: 12, left: 4, bottom: 36 };
const lineMargin = { top: 28, right: 16, left: 4, bottom: 36 };

const legendProps = {
    verticalAlign: "top",
    align: "right",
    iconSize: 10,
    height: 22,
    wrapperStyle: { fontSize: 11, lineHeight: "20px" },
};

function SummaryBarChart({ data, maxKey, actualKey }) {
    return (
        <ChartBox>
            <BarChart data={data} margin={barMargin} barCategoryGap="28%" barGap={4}>
                <CartesianGrid {...chartGrid} />
                <XAxis dataKey="name" interval={0} tick={axisTick} tickMargin={8} height={32} />
                <YAxis domain={[0, "auto"]} tick={axisTick} width={36} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend {...legendProps} />
                <Bar dataKey={maxKey} fill={DPAT_CHART.navy} name="Maximum" radius={[3, 3, 0, 0]} maxBarSize={36} isAnimationActive={false} />
                <Bar dataKey={actualKey} fill={DPAT_CHART.teal} name="Final score" radius={[3, 3, 0, 0]} maxBarSize={36} isAnimationActive={false} />
            </BarChart>
        </ChartBox>
    );
}

function CompliancePieChart({ data, totalCount }) {
    return (
        <ChartBox height={240}>
            <PieChart margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
                <Pie
                    data={data}
                    cx="50%"
                    cy="42%"
                    labelLine={false}
                    label={({ name, value }) => (value ? `${name} ${value}/${totalCount}` : "")}
                    outerRadius={72}
                    dataKey="value"
                    isAnimationActive={false}
                >
                    {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} / ${totalCount}`, "Count"]} contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
        </ChartBox>
    );
}

const ScoreSheetSummary = ({ district, districtId, year, hideRoleScoreColumns = false }) => {
    const { user } = useAuth();
    const { data: response, isLoading, error } = useGetScoreSheetSummary(
        districtId ?? district?.value,
        year,
        "DPAT",
    );

    const roleColumnVisibility = useMemo(() => {
        if (hasDpatFullScoreAccess(user)) {
            return { technical: true, qualityAssurance: true, petition: true };
        }
        if (hideRoleScoreColumns || isDpatDistrictRestrictedViewer(user)) {
            return { technical: false, qualityAssurance: false, petition: false };
        }

        const roleNames = user?.user?.userRoles?.map((role) => role.name) ?? [];
        const isPetition = roleNames.includes("DPAT PETITION COMMITTEE") || roleNames.includes("Superuser");
        const isQa = roleNames.includes("DPAT QUALITY ASSURANCE");
        const isTechnical = roleNames.includes("DPAT TECHNICAL TEAM");

        if (isPetition) {
            return { technical: true, qualityAssurance: true, petition: true };
        }
        if (isQa) {
            return { technical: true, qualityAssurance: true, petition: false };
        }
        if (isTechnical) {
            return { technical: true, qualityAssurance: false, petition: false };
        }

        // Default for other viewers who can still open the sheet: system + final only
        return { technical: false, qualityAssurance: false, petition: false };
    }, [hideRoleScoreColumns, user]);

    const showSystemColumns = hasDpatFullScoreAccess(user) || !(hideRoleScoreColumns || isDpatDistrictRestrictedViewer(user));

    const visibleRoleColumnCount =
        Number(roleColumnVisibility.technical) +
        Number(roleColumnVisibility.qualityAssurance) +
        Number(roleColumnVisibility.petition);

    if (isLoading && !response) {
        return null;
    }

    if (error) {
        console.error("Error loading summary:", error);
        return (
            <div className="text-center py-5 text-danger">
                Error loading assessment summary. Please try again.
            </div>
        );
    }

    if (!response) {
        return (
            <div className="text-center py-5">
                No assessment data available.
            </div>
        );
    }

    // Summary already includes merged manual scores + final-score cascade
    const summaryData = response.data || response || {};

    const { complianceIndicators, serviceDeliveryIndicators, performanceIndicators, totals } = summaryData;

    const mainFulfilledCount = countFulfilled(complianceIndicators);
    const mainComplianceChartData = buildComplianceChartData(mainFulfilledCount, TOTAL_MAIN_CI_COUNT);

    // SDI Performance Data
    const sdiPerformanceData = serviceDeliveryIndicators
        ?.filter(item => item.no !== "Sub-total (SDIs)")
        ?.map(item => ({
            name: item.no,
            maxScore: item.maxScore,
            actualScore: item.actualScore,
            percentage: ((item.actualScore / item.maxScore) * 100).toFixed(1)
        })) || [];

    // PI Performance Data
    const piPerformanceData = performanceIndicators
        ?.filter(item => item.no !== "Sub-total (PIs)")
        ?.map(item => ({
            name: item.no,
            maxScore: item.maxScore,
            actualScore: item.actualScore,
            percentage: ((item.actualScore / item.maxScore) * 100).toFixed(1)
        })) || [];

    // Overall Performance Data
    const overallData = [
        { name: 'SDIs', actual: totals?.sdiTotalActualScore || 0, max: totals?.sdiTotalMaxScore || 0 },
        { name: 'PIs', actual: totals?.piTotalActualScore || 0, max: totals?.piTotalMaxScore || 0 }
    ];

    const overallPercentage = ((totals?.grandTotalActualScore || 0) / (totals?.grandTotalMaxScore || 1)) * 100;
    const districtLabel = district?.label || district?.name || "District";
    // CI: No, Thematic, System Outcome, [role cols], Final Outcome
    const ciColumnCount = (showSystemColumns ? 4 : 3) + visibleRoleColumnCount;
    // SDI/PI: No, Thematic, Max, [System], [role cols], Final
    const scoredColumnCount = (showSystemColumns ? 5 : 4) + visibleRoleColumnCount;

    return (
        <div>


            <div className="dpat-summary-sheet">
            <div data-pdf-part="summary-overview">
            <header className="dpat-summary-hero">
                <p>DPAT Assessment</p>
                <h2>Summary Scoring Sheet for DPAT Indicators</h2>
                <span>{districtLabel} · {year}</span>
            </header>

            <div className="dpat-summary-kpis">
                <KpiTile
                    label="Overall Score"
                    value={`${totals?.grandTotalActualScore || 0} / ${totals?.grandTotalMaxScore || 0}`}
                    tone="navy"
                />
                <KpiTile
                    label="Overall Percentage"
                    value={`${overallPercentage.toFixed(1)}%`}
                    tone={isAtOrAbovePassMark(overallPercentage) ? "good" : "alert"}
                />
                <KpiTile
                    label="Service Delivery (SDI)"
                    value={`${totals?.sdiTotalActualScore || 0} / ${totals?.sdiTotalMaxScore || 0}`}
                    tone="teal"
                />
                <KpiTile
                    label="Performance (PI)"
                    value={`${totals?.piTotalActualScore || 0} / ${totals?.piTotalMaxScore || 0}`}
                    tone="amber"
                />
            </div>

            {complianceIndicators && complianceIndicators.length > 0 && (
                <ChartPanel title="Compliance Indicators">
                    <CompliancePieChart
                        data={mainComplianceChartData}
                        totalCount={TOTAL_MAIN_CI_COUNT}
                    />
                </ChartPanel>
            )}
            </div>

            <div className="dpat-summary-chart-grid">
            <div className="dpat-summary-chart-page" data-pdf-part="summary-charts-1">
            <ChartPanel title="Overall SDI / PI Comparison">
                <SummaryBarChart data={overallData} maxKey="max" actualKey="actual" />
            </ChartPanel>

            {sdiPerformanceData.length > 0 && (
                <ChartPanel title="Service Delivery Indicators">
                    <SummaryBarChart data={sdiPerformanceData} maxKey="maxScore" actualKey="actualScore" />
                </ChartPanel>
            )}
            </div>

            <div className="dpat-summary-chart-page" data-pdf-part="summary-charts-2">
            {piPerformanceData.length > 0 && (
                <ChartPanel title="Performance Indicators">
                    <SummaryBarChart data={piPerformanceData} maxKey="maxScore" actualKey="actualScore" />
                </ChartPanel>
            )}

            {sdiPerformanceData.length > 0 && piPerformanceData.length > 0 && (
                <ChartPanel title="Percentage by Indicator">
                    <ChartBox>
                        <LineChart margin={lineMargin}>
                            <CartesianGrid {...chartGrid} />
                            <XAxis dataKey="name" type="category" allowDuplicatedCategory={false} interval={0} tick={axisTick} tickMargin={8} height={32} />
                            <YAxis domain={[0, 100]} tick={axisTick} width={36} />
                            <Tooltip contentStyle={tooltipStyle} />
                            <Legend {...legendProps} />
                            <Line data={sdiPerformanceData} dataKey="percentage" stroke={DPAT_CHART.navy} name="SDI %" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                            <Line data={piPerformanceData} dataKey="percentage" stroke={DPAT_CHART.teal} name="PI %" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                        </LineChart>
                    </ChartBox>
                </ChartPanel>
            )}
            </div>
            </div>
            </div>

            <div className="table-responsive" data-pdf-part="summary-table">
                <h3 className="dpat-summary-table-heading">Indicator Score Table</h3>
                <table className="table table-bordered" style={{
                    border: '1px solid #000',
                    borderCollapse: 'collapse',
                    width: '100%',
                    marginTop: "8px"
                }}>
                    <thead>
                        <tr style={{ fontWeight: 'bold', border: '1px solid #000' }}>
                            <th style={{ border: '1px solid #000', fontWeight: 'bold', width: '12%' }}>No.</th>
                            <th style={{ border: '1px solid #000', fontWeight: 'bold', width: '34%' }}>Thematic Area</th>
                            {showSystemColumns && (
                            <th style={{ border: '1px solid #000', fontWeight: 'bold', width: '12%' }}>System Outcome</th>
                            )}
                            {roleColumnVisibility.technical && (
                                <th style={{ border: '1px solid #000', fontWeight: 'bold', width: '10%' }}>Technical Team</th>
                            )}
                            {roleColumnVisibility.qualityAssurance && (
                                <th style={{ border: '1px solid #000', fontWeight: 'bold', width: '10%' }}>Quality Assurance</th>
                            )}
                            {roleColumnVisibility.petition && (
                                <th style={{ border: '1px solid #000', fontWeight: 'bold', width: '10%' }}>Complaints Resolution</th>
                            )}
                            <th style={{ border: '1px solid #000', fontWeight: 'bold', width: '12%' }}>Final Outcome</th>
                        </tr>
                    </thead>

                    {/* Compliance Indicators Section */}
                    <tbody>
                        <tr style={{ backgroundColor: '#ccccc9f2' }}>
                            <td colSpan={ciColumnCount} style={{ border: '1px solid #000', fontWeight: 'bold' }}>
                                SECTION A: COMPLIANCE INDICATORS (CI)
                            </td>
                        </tr>
                        {complianceIndicators && complianceIndicators.length > 0 ? (
                            complianceIndicators.map((item, index) => (
                                <tr key={index}>
                                    <td style={{ border: '1px solid #000' }}>
                                        <strong>{item.no}</strong>
                                    </td>
                                    <td style={{ border: '1px solid #000' }}>{item.thematicArea}</td>
                                    {showSystemColumns && (
                                    <td style={{ border: '1px solid #000' }}>
                                        {ciOutcomeCell(Boolean(item.systemFulfilled))}
                                    </td>
                                    )}
                                    {roleColumnVisibility.technical && (
                                        <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                            {roleManualScoreCell(item, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM, "CI")}
                                        </td>
                                    )}
                                    {roleColumnVisibility.qualityAssurance && (
                                        <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                            {roleManualScoreCell(item, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE, "CI")}
                                        </td>
                                    )}
                                    {roleColumnVisibility.petition && (
                                        <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                            {roleManualScoreCell(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE, "CI")}
                                        </td>
                                    )}
                                    <td style={{ border: '1px solid #000' }}>
                                        {ciOutcomeCell(Boolean(item.fulfilled))}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={ciColumnCount} style={{ border: '1px solid #000', textAlign: 'center' }}>
                                    No compliance indicators data available
                                </td>
                            </tr>
                        )}
                    </tbody>

                    {/* Service Delivery Indicators Section */}
                    <tbody>
                        <tr style={{ backgroundColor: '#ccccc9f2' }}>
                            <td colSpan="2" style={{ border: '1px solid #000', fontWeight: 'bold' }}>
                                SECTION B: SERVICE DELIVERY INDICATORS (SDIs)
                            </td>
                            <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Max Score</td>
                            {showSystemColumns && (
                            <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>System Score</td>
                            )}
                            {roleColumnVisibility.technical && (
                                <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Technical Team</td>
                            )}
                            {roleColumnVisibility.qualityAssurance && (
                                <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Quality Assurance</td>
                            )}
                            {roleColumnVisibility.petition && (
                                <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Petition Committee</td>
                            )}
                            <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Final Score</td>
                        </tr>
                        {serviceDeliveryIndicators && serviceDeliveryIndicators.length > 0 ? (
                            serviceDeliveryIndicators.map((item, index) => (
                                <tr key={index} style={item.no === "Sub-total (SDIs)" ? { backgroundColor: '#f0f0f0', fontWeight: 'bold' } : {}}>
                                    <td style={{ border: '1px solid #000' }}>
                                        <strong>{item.no}</strong>
                                    </td>
                                    <td style={{ border: '1px solid #000' }}>{item.thematicArea}</td>
                                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                        <strong>{item.maxScore}</strong>
                                    </td>
                                    {showSystemColumns && (
                                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                        <strong>{systemScoreCell(item)}</strong>
                                    </td>
                                    )}
                                    {roleColumnVisibility.technical && (
                                        <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                            {roleManualScoreCell(item, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM)}
                                        </td>
                                    )}
                                    {roleColumnVisibility.qualityAssurance && (
                                        <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                            {roleManualScoreCell(item, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE)}
                                        </td>
                                    )}
                                    {roleColumnVisibility.petition && (
                                        <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                            {roleManualScoreCell(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE)}
                                        </td>
                                    )}
                                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                        <strong>{item.actualScore}</strong>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={scoredColumnCount} style={{ border: '1px solid #000', textAlign: 'center' }}>
                                    No service delivery indicators data available
                                </td>
                            </tr>
                        )}
                    </tbody>

                    {/* Performance Indicators Section */}
                    <tbody>
                        <tr style={{ backgroundColor: '#ccccc9f2' }}>
                            <td colSpan="2" style={{ border: '1px solid #000', fontWeight: 'bold' }}>
                                SECTION C: PERFORMANCE INDICATORS (PIs)
                            </td>
                            <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Max Score</td>
                            {showSystemColumns && (
                            <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>System Score</td>
                            )}
                            {roleColumnVisibility.technical && (
                                <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Technical Team</td>
                            )}
                            {roleColumnVisibility.qualityAssurance && (
                                <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Quality Assurance</td>
                            )}
                            {roleColumnVisibility.petition && (
                                <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Petition Committee</td>
                            )}
                            <td style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }}>Final Score</td>
                        </tr>
                        {performanceIndicators && performanceIndicators.length > 0 ? (
                            performanceIndicators.map((item, index) => (
                                <tr key={index} style={item.no === "Sub-total (PIs)" ? { backgroundColor: '#f0f0f0', fontWeight: 'bold' } : {}}>
                                    <td style={{ border: '1px solid #000' }}>
                                        <strong>{item.no}</strong>
                                    </td>
                                    <td style={{ border: '1px solid #000' }}>{item.thematicArea}</td>
                                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                        <strong>{item.maxScore}</strong>
                                    </td>
                                    {showSystemColumns && (
                                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                        <strong>{systemScoreCell(item)}</strong>
                                    </td>
                                    )}
                                    {roleColumnVisibility.technical && (
                                        <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                            {roleManualScoreCell(item, MANUAL_SCORE_FIELDS.TECHNICAL_TEAM)}
                                        </td>
                                    )}
                                    {roleColumnVisibility.qualityAssurance && (
                                        <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                            {roleManualScoreCell(item, MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE)}
                                        </td>
                                    )}
                                    {roleColumnVisibility.petition && (
                                        <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                            {roleManualScoreCell(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE)}
                                        </td>
                                    )}
                                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                        <strong>{item.actualScore}</strong>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={scoredColumnCount} style={{ border: '1px solid #000', textAlign: 'center' }}>
                                    No performance indicators data available
                                </td>
                            </tr>
                        )}

                        {/* Grand Total Row */}
                        {totals && (
                            <tr style={{ backgroundColor: '#e6f7ff', fontWeight: 'bold' }}>
                                <td colSpan="2" style={{ border: '1px solid #000', textAlign: 'right' }}>
                                    <strong>TOTAL (SDI + PIs)</strong>
                                </td>
                                <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                    <strong>{totals.grandTotalMaxScore || 0}</strong>
                                </td>
                                {showSystemColumns && (
                                <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                    <strong>{totals.grandTotalSystemScore ?? 0}</strong>
                                </td>
                                )}
                                {roleColumnVisibility.technical && (
                                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                        <strong>{totals.technicalTeamTotalScore ?? 0}</strong>
                                    </td>
                                )}
                                {roleColumnVisibility.qualityAssurance && (
                                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                        <strong>{totals.qualityAssuranceTotalScore ?? 0}</strong>
                                    </td>
                                )}
                                {roleColumnVisibility.petition && (
                                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                        <strong>{totals.petitionCommitteeTotalScore ?? 0}</strong>
                                    </td>
                                )}
                                <td style={{ border: '1px solid #000', textAlign: 'center' }}>
                                    <strong>{totals.grandTotalActualScore || 0}</strong>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ScoreSheetSummary;